import {Box, IconButton, Paper, Toolbar, Typography} from "@mui/material";
import {Close, Menu, Minimize} from "@mui/icons-material";
import React, {DragEventHandler, ReactElement, useState} from "react";
import makeStyles from "@mui/styles/makeStyles";

const useStyles = makeStyles(theme => ({
    content: {
        height: "100%",
        overflowY: "scroll",
        '&::-webkit-scrollbar': {
            width: '0.4em'
        },
        '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(0,0,0,.1)',
            borderRadius: '0.4em'
        }
    },
    paper: {
        position: "absolute",
        height: "100%",
        maxWidth: "50%",
        margin: theme.spacing(1),
        pointerEvents: "all",
        zIndex: 100
    },
    dialogContent: {
        display: "flex",
        flexDirection: "column",
        height: "100%",
    },
    dialogMinimised: {
        height: 47,
    },
    header: {
        backgroundColor: "Background",
        borderTopLeftRadius: 4,
        borderTopRightRadius: 4,
        color: theme.palette.primary.contrastText,
        cursor: "grab",
        "& :active": {
            cursor: "grabbing"
        }
    },
    title: {
        marginLeft: theme.spacing(2),
        pointerEvents: "none",
        userSelect: "none"
    },
    toolbar: {
        backgroundColor: theme.palette.primary.main,
        borderRadius: 4,
    },
    tools: {
        marginLeft: theme.spacing(1),
        marginRight: theme.spacing(1)
    }
}));

interface IProps {
    children: ReactElement | ReactElement []
    onClose?: () => void
    title: string
    tools?: ReactElement
}

export function FloatingDialog({children, onClose, title, tools}: IProps) {
    const classes = useStyles();
    const [dragStart, setDragStart] = useState({left: 0, top: 0});
    const [minimised, setMinimised] = useState(false);
    const [offset, setOffset] = useState({left: 0, top: 0});
    const handleToggleMinimised = () => setMinimised(! minimised);
    const handleDrag: DragEventHandler<HTMLDivElement> = ({screenX, screenY}) => {
        if (screenX && screenY) {
            setOffset({left: screenX - dragStart.left, top: screenY - dragStart.top});
        }
    }
    const handleDragStart: DragEventHandler<HTMLDivElement> = ({screenX, screenY}) => {
        setDragStart({left: screenX - offset.left, top: screenY - offset.top});
    }
    const handleDragEnd: DragEventHandler<HTMLDivElement> = ({screenX, screenY}) => {
        if (screenX && screenY) {
            setOffset({left: screenX - dragStart.left, top: screenY - dragStart.top});
        }
    };

    return (
        <Paper className={[classes.paper, minimised ? classes.dialogMinimised : ""].join(" ")} style={offset}>
            <Box className={classes.dialogContent}>
                <Box
                    position="relative" className={classes.header} borderRadius={minimised ? "4px" : "0"}
                    draggable onDrag={handleDrag} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
                    <Toolbar className={classes.toolbar} variant="dense">
                        <IconButton size="small" edge="start" color="inherit" onClick={handleToggleMinimised}>
                            {minimised ? <Menu/> : <Minimize/>}
                        </IconButton>
                        <Box flexGrow={1}>
                            <Typography className={classes.title} variant="h6">{title}</Typography>
                        </Box>
                        {!minimised && tools && <Box className={classes.tools}>{tools}</Box>}
                        {onClose && minimised && <IconButton size="small" edge="end" color="inherit" onClick={onClose}>
                            <Close/>
                        </IconButton>}
                    </Toolbar>
                </Box>
                {!minimised && <Box className={classes.content}>{children}</Box>}
            </Box>
        </Paper>
    );
}